import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { tr } from "@/i18n";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { FgLogo } from "@/components/FgLogo";
import { SoccorsoCard, IncidentWizard, MyIncidents, ContrattiCard, VerifyEmailCard, ProposalsCard, GestionaleCard } from "@/components/AccountExtra";
import { CalendarDays, Check, CreditCard, FileText, Home as HomeIcon, Loader2, LogOut, Mail, Pencil, Phone, Trash2, TriangleAlert, User, UserRound, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fr_customer_token";

const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">{label}</span>
    <input
      {...props}
      className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
    />
  </label>
);

const AuthForm = ({ onLogin }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(url, body);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-card border border-gray-100 p-6" data-testid="account-auth-form">
        <div className="flex items-center gap-3 mb-5">
          <FgLogo size={40} />
          <div className="leading-tight">
            <p className="font-extrabold text-fr-navy uppercase tracking-tight">{mode === "login" ? "Le mie informazioni" : "Crea il tuo account"}</p>
            <p className="text-[11px] text-gray-400">Dati salvati, prenotazioni, documenti e offerte</p>
          </div>
        </div>
        {mode === "register" && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Field label="Nome *" value={form.first_name} onChange={set("first_name")} placeholder="Michele" data-testid="account-input-first-name" />
              <Field label="Cognome *" value={form.last_name} onChange={set("last_name")} placeholder="Fabricino" data-testid="account-input-last-name" />
            </div>
            <div className="mb-3">
              <Field label="Telefono *" value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" data-testid="account-input-phone" />
            </div>
          </>
        )}
        <div className="mb-3">
          <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="tu@email.it" data-testid="account-input-email" />
        </div>
        <label className="block mb-4">
          <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">Password * (min 8)</span>
          <input
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={set("password")}
            className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white"
            placeholder="••••••••"
            data-testid="account-input-password"
          />
        </label>
        {error && <p className="mb-3 text-xs text-fr-red font-semibold" data-testid="account-auth-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-fr-navy text-white font-bold text-sm py-3 hover:bg-fr-navydark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="account-auth-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <UserRound size={15} />}
          {mode === "login" ? "Accedi" : "Crea account"}
        </button>
        <button
          type="button"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          className="w-full mt-3 text-xs font-semibold text-gray-500 hover:text-fr-navy underline underline-offset-2"
          data-testid="account-auth-switch"
        >
          {mode === "login" ? "Non hai un account? Registrati" : "Hai già un account? Accedi"}
        </button>
      </form>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, onEdit, testid }) => (
  <div className="flex items-center gap-3 py-3.5 border-b border-gray-100 last:border-0" data-testid={testid}>
    <Icon size={18} className="text-fr-navy shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-bold text-fr-ink text-sm leading-tight">{label}</p>
      <p className="text-xs text-gray-500 truncate">{value || <span className="italic text-gray-300">da inserire</span>}</p>
    </div>
    {onEdit && (
      <button onClick={onEdit} aria-label={`Modifica ${label}`} className="p-2 text-gray-400 hover:text-fr-navy transition-colors" data-testid={`${testid}-edit`}>
        <Pencil size={15} />
      </button>
    )}
  </div>
);

const DocCard = ({ docType, title, docPath, token, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState(null);
  const inputRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    let url = null;
    let active = true;
    if (docPath) {
      axios
        .get(`${API}/account/files/${docPath}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }, responseType: "blob" })
        .then((r) => { if (!active) return; url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => active && setSrc(null));
    } else setSrc(null);
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [docPath]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/account/documents/${docType}`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento salvato: il gestionale lo verifica a breve");
      onUploaded();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/account/documents/${docType}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      toast.success("Documento rimosso");
      onUploaded();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3.5" data-testid={`doc-card-${docType}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={16} className="text-fr-navy shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-bold text-fr-ink">{title}</p>
            <p className="text-[10px] text-gray-400">{docPath ? "Caricato — in verifica gestionale" : "Carica la foto del fronte"}</p>
          </div>
        </div>
        {docPath && (
          <button onClick={remove} className="p-1.5 text-fr-red hover:text-red-700" aria-label="Rimuovi documento" data-testid={`doc-remove-${docType}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <div className="mt-2.5 aspect-[4/3] rounded-xl bg-gray-50 border border-dashed border-gray-200 overflow-hidden flex items-center justify-center">
        {src ? (
          <img src={src} alt={title} className="w-full h-full object-cover" data-testid={`doc-preview-${docType}`} />
        ) : (
          <FileText size={26} className="text-gray-300" />
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={`doc-camera-${docType}`} />
      <div className="mt-2.5 grid grid-cols-2 gap-2">
        <button
          onClick={() => camRef.current?.click()}
          disabled={busy}
          className="rounded-lg border border-fr-navy text-fr-navy text-xs font-bold py-2 hover:bg-fr-navy hover:text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid={`doc-camera-btn-${docType}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Scatta una foto
        </button>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded-lg border border-fr-navy text-fr-navy text-xs font-bold py-2 hover:bg-fr-navy hover:text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid={`doc-upload-${docType}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : null}
          {docPath ? "Sostituisci foto" : "Allega documento"}
        </button>
      </div>
    </div>
  );
};

export default function AccountPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pms, setPms] = useState([]);
  const [pmForm, setPmForm] = useState({ card_holder: "", last4: "", brand: "Visa", expiry: "" });
  const [edit, setEdit] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showIncident, setShowIncident] = useState(false);

  const authApi = () =>
    axios.create({ baseURL: `${API}/account`, headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const load = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setUser(data);
        authApi().get("/bookings").then(({ data: b }) => setBookings(b.bookings)).catch(() => {});
        authApi().get("/payment-methods").then(({ data: p }) => setPms(p.payment_methods)).catch(() => {});
      })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const startEdit = (field, value) => {
    setEdit(field);
    setEditVal(value || "");
  };

  const saveEdit = async () => {
    try {
      const { data } = await authApi().patch("/profile", { [edit]: editVal });
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Informazione aggiornata");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const addPm = async () => {
    if (!pmForm.card_holder || pmForm.last4.length !== 4 || !pmForm.expiry) {
      toast.error("Compila: intestatario, 4 cifre finali e scadenza");
      return;
    }
    try {
      await authApi().post("/payment-methods", pmForm);
      toast.success("Metodo di pagamento salvato (solo ultime cifre: mai il numero completo)");
      setPmForm({ card_holder: "", last4: "", brand: "Visa", expiry: "" });
      authApi().get("/payment-methods").then(({ data }) => setPms(data.payment_methods));
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const removePm = async (id) => {
    try {
      await authApi().delete(`/payment-methods/${id}`);
      setPms(pms.filter((p) => p.id !== id));
      toast.success("Metodo rimosso");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const deleteAccount = async () => {
    try {
      await authApi().delete("/account");
      toast.success("Account eliminato");
      logout();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const token = user ? localStorage.getItem(TOKEN_KEY) : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-page">
      <header className="bg-fr-navy text-white h-14 flex items-center px-4 sticky top-0 z-20">
        <a href="/" className="flex items-center gap-2.5" data-testid="account-home-link">
          <FgLogo size={32} />
          <span className="font-extrabold tracking-tight">Area clienti FabriRent</span>
        </a>
        {user && (
          <button onClick={logout} className="ml-auto flex items-center gap-1.5 text-xs font-semibold bg-white/10 rounded-lg px-3 py-2 hover:bg-white/20" data-testid="account-logout">
            <LogOut size={13} /> Esci
          </button>
        )}
      </header>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 size={28} className="animate-spin text-fr-navy" /></div>
      ) : !user ? (
        <AuthForm onLogin={(u) => { setUser(u); load(); }} />
      ) : (
        <main className="max-w-2xl mx-auto p-4 space-y-6">
          <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="account-info">
            <h1 className="text-xl font-extrabold tracking-tight text-fr-ink uppercase" data-testid="account-info-title">{tr("Le mie informazioni")}</h1>
            {edit ? (
              <div className="mt-4 rounded-xl border border-fr-navy/30 bg-gray-50 p-3" data-testid="account-edit-box">
                <Field
                  label={edit === "first_name" ? "Nome" : edit === "last_name" ? "Cognome" : edit === "phone" ? "Telefono cellulare" : edit === "address" ? "Indirizzo di casa" : edit === "license_number" ? "Numero patente" : edit === "license_expiry" ? "Scadenza patente (MM/AA)" : "Email"}
                  type={edit === "email" ? "email" : "text"}
                  value={editVal}
                  onChange={(e) => setEditVal(e.target.value)}
                  placeholder="Nuovo valore…"
                  data-testid="account-edit-input"
                />
                <div className="mt-2 flex gap-2">
                  <button onClick={saveEdit} className="rounded-lg bg-fr-navy text-white text-xs font-bold px-4 py-2 flex items-center gap-1.5" data-testid="account-edit-save"><Check size={13} /> Salva</button>
                  <button onClick={() => setEdit(null)} className="rounded-lg bg-gray-100 text-gray-600 text-xs font-bold px-4 py-2" data-testid="account-edit-cancel"><X size={13} /> Annulla</button>
                </div>
              </div>
            ) : null}

            <div className="mt-2">
              <InfoRow icon={User} label="Nome" value={`${user.first_name} ${user.last_name}`} onEdit={() => startEdit("first_name", user.first_name)} testid="info-name" />
              <InfoRow icon={Mail} label="Email" value={user.email} onEdit={() => startEdit("email", user.email)} testid="info-email" />
              <InfoRow icon={Phone} label="Telefono cellulare" value={user.phone} onEdit={() => startEdit("phone", user.phone)} testid="info-phone" />
              <InfoRow icon={HomeIcon} label="Indirizzo di casa" value={user.address} onEdit={() => startEdit("address", user.address)} testid="info-address" />
              <InfoRow
                icon={CreditCard}
                label="Metodo di pagamento"
                value={pms[0] ? `${pms[0].brand} •••• ${pms[0].last4}` : ""}
                onEdit={() => document.getElementById("account-payment-methods")?.scrollIntoView({ behavior: "smooth" })}
                testid="info-payment"
              />
              <InfoRow icon={FileText} label="Patente di guida" value={user.license_number ? `N. ${user.license_number} · scad. ${user.license_expiry || "?"}` : ""} onEdit={() => startEdit("license_number", user.license_number)} testid="info-license" />
            </div>
          </section>

          <section data-testid="account-documents">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{tr("Documenti (carta d'identità e patente)")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DocCard docType="identity_card" title="Carta d'identità" docPath={user.documents?.identity_card?.path} token={token} onUploaded={load} />
              <DocCard docType="driver_license" title="Patente di guida" docPath={user.documents?.driver_license?.path} token={token} onUploaded={load} />
            </div>
            {user.docs && Object.entries(user.docs).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="account-docs-status">
                {Object.entries(user.docs).map(([k, v]) => (
                  <span key={k} className={`text-[10px] font-bold rounded px-2 py-0.5 ${v.status === "accettato" ? "bg-green-100 text-green-800" : v.status === "rifiutato" || v.status === "da_integrare" ? "bg-red-100 text-fr-red" : "bg-amber-100 text-amber-800"}`} data-testid={`doc-status-badge-${k}`}>
                    {k.replace("_", " ")}: {v.status}{v.expires_at ? ` (scad. ${v.expires_at})` : ""}{v.reject_reason ? ` — ${v.reject_reason}` : ""}
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-gray-400 mt-2">I documenti sono visibili solo a te e al gestionale: vengono usati per la pratica di noleggio. La conferma del noleggio richiede patente e documento accettati dall'operatore e non scaduti.</p>
          </section>

          <VerifyEmailCard user={user} onVerified={load} />
          <GestionaleCard />
          <ProposalsCard />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="account-help-sections">
            <SoccorsoCard activeTarga={bookings.find((b) => ["confermata", "in_corso"].includes(b.status) && b.targa_assigned)?.targa_assigned || ""} />
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="account-incident-card">
              <div className="flex items-center gap-2">
                <TriangleAlert size={17} className="text-fr-red" />
                <p className="font-extrabold text-fr-navy text-sm">{tr("Ho avuto un incidente")}</p>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">{tr("Procedura guidata: documenti, foto danni (min 4), GPS e la tua versione dei fatti. Pratica inviata subito al gestionale.")}</p>
              <button onClick={() => setShowIncident(true)} className="mt-3 w-full rounded-xl bg-fr-red text-white font-extrabold text-sm py-3 hover:bg-red-700 transition-colors" data-testid="account-incident-open">
                Apri la procedura guidata
              </button>
              {showIncident && <IncidentWizard bookings={bookings.filter((b) => b.status !== "annullata")} onClose={() => setShowIncident(false)} onCreated={load} />}
            </div>
          </div>

          <MyIncidents />
          <ContrattiCard bookings={bookings.filter((b) => ["confermata", "in_corso", "rientrata", "conclusa"].includes(b.status))} />

          <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="account-bookings">
            <p className="flex items-center gap-2 font-extrabold text-fr-navy"><CalendarDays size={16} /> Le tue prenotazioni</p>
            {bookings.length === 0 && (
              <p className="text-xs text-gray-400 mt-2">Nessuna prenotazione: <a href="/" className="underline text-fr-navy">prenota ora</a> — consegna da 5 a 90 minuti.</p>
            )}
            <div className="mt-3 space-y-2">
              {bookings.map((b) => (
                <div key={b.reference} className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs" data-testid={`account-booking-${b.reference}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-extrabold text-fr-navy">{b.reference}</span>
                    <span className="text-[10px] font-bold bg-fr-ink text-white rounded px-1.5 py-0.5">{b.category}</span>
                    {b.preferred_model && <span className="text-[10px] text-gray-500">{b.preferred_model}</span>}
                    <span className={`ml-auto text-[10px] font-bold rounded px-1.5 py-0.5 ${["confermata", "in_corso", "rientrata", "conclusa"].includes(b.status) ? "bg-green-100 text-green-800" : ["annullata", "no_show"].includes(b.status) ? "bg-red-100 text-fr-red" : "bg-amber-100 text-amber-800"}`}>{b.status.replace("_", " ")}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {b.location_name} • {format(new Date(b.pickup_at), "d MMM HH:mm", { locale: it })} → {format(new Date(b.dropoff_at), "d MMM HH:mm", { locale: it })} • {b.days} giorni
                    {b.targa_assigned && <> • targa <b className="text-fr-navy">{b.targa_assigned}</b></>}
                    {b.total_price != null && <> • totale <b className="text-fr-navy">€ {b.total_price}</b></>}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="account-payment-methods" id="account-payment-methods">
            <p className="flex items-center gap-2 font-extrabold text-fr-navy"><CreditCard size={16} /> Metodi di pagamento</p>
            <p className="text-[10px] text-gray-400 mt-1">Salviamo SOLO marche e ultime 4 cifre: mai il numero completo. Il pagamento avviene dopo la conferma del gestionale.</p>
            {pms.length > 0 && (
              <ul className="mt-3 space-y-2">
                {pms.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs" data-testid="account-pm-row">
                    <CreditCard size={15} className="text-fr-navy" />
                    <span className="font-semibold">{p.brand} •••• {p.last4}</span>
                    <span className="text-gray-400">{p.card_holder} • {p.expiry}</span>
                    <button onClick={() => removePm(p.id)} className="ml-auto text-fr-red hover:text-red-700" aria-label="Rimuovi"><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Field label="Intestatario" value={pmForm.card_holder} onChange={(e) => setPmForm({ ...pmForm, card_holder: e.target.value })} placeholder="Michele Fabricino" data-testid="account-pm-holder" />
              <label className="block">
                <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">Circuito</span>
                <select value={pmForm.brand} onChange={(e) => setPmForm({ ...pmForm, brand: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="account-pm-brand">
                  {["Visa", "Mastercard", "Amex", "Carta Si"].map((b) => <option key={b}>{b}</option>)}
                </select>
              </label>
              <Field label="Ultime 4 cifre" value={pmForm.last4} onChange={(e) => setPmForm({ ...pmForm, last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="4242" data-testid="account-pm-last4" />
              <Field label="Scadenza (MM/AA)" value={pmForm.expiry} onChange={(e) => setPmForm({ ...pmForm, expiry: e.target.value.slice(0, 5) })} placeholder="11/35" data-testid="account-pm-expiry" />
            </div>
            <button onClick={addPm} className="mt-3 rounded-xl bg-fr-navy text-white font-bold text-sm px-5 py-2.5 hover:bg-fr-navydark" data-testid="account-pm-save">Salva metodo</button>
          </section>

          <section className="pb-10 flex justify-center" data-testid="account-danger-zone">
            {confirmDelete ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                <p className="text-xs text-red-800 font-semibold">Eliminare definitivamente l'account? Le prenotazioni restano nel gestionale.</p>
                <div className="mt-2 flex gap-2 justify-center">
                  <button onClick={deleteAccount} className="rounded-lg bg-fr-red text-white text-xs font-bold px-4 py-2" data-testid="account-delete-confirm">Sì, elimina</button>
                  <button onClick={() => setConfirmDelete(false)} className="rounded-lg bg-white border border-gray-200 text-gray-600 text-xs font-bold px-4 py-2" data-testid="account-delete-cancel">Annulla</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 text-xs font-bold text-fr-red underline underline-offset-2" data-testid="account-delete-button">
                <X size={13} /> Elimina account
              </button>
            )}
          </section>
        </main>
      )}
    </div>
  );
}
